import React from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import UserDetailsFields from '@/pages/settings/details/UserDetailsFields';
import { Button } from '@/components/buttons/Button';
import { selectIsFastReg, selectTagsLoaded } from '@/store/selectors/userSelectors';
import { openZendesk } from '@/components/Zendesk';
import { openModal } from '@/store/actions/modal';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { User } from '@/models/user.model';
import { envIsMexico } from '@/constants/constants';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';

const UserDetails = ({ user }: { user: User }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const hasFastRegTag = useSelector(selectIsFastReg);
  const tagsLoaded = useSelector(selectTagsLoaded);
  const navigate = useAbsoluteNavigate();

  const handleOnCompleteProfileClick = () => {
    triggerGTag(GTagEvents.complete_profile_details_click);
    // for Mexico we want to redirect the user to the Verify tab as we don't have the complete profile modal solved
    if (envIsMexico()) {
      navigate('/settings/verify');
      return;
    }
    dispatch(openModal('completeProfile'));
  };

  if (!tagsLoaded) {
    return null;
  }

  // If user has any fast reg tag show the user complete profile dialog
  if (hasFastRegTag) {
    return (
      <section
        className="mx-auto mb-4 max-w-[720px] flex flex-wrap justify-between py-8 px-4 bg-white rounded-md
        sm:p-8 sm:pb-8"
      >
        <div className="block w-full text-center">
          <h3>{intl.formatMessage({ id: 'completeProfile.prompt.header' })}</h3>
          <p className="text-base opacity-80">
            {intl.formatMessage({ id: 'completeProfile.prompt.text' })}
          </p>
          <Button
            text={intl.formatMessage({ id: 'completeProfile.prompt.button' })}
            onClickHandler={handleOnCompleteProfileClick}
          />
        </div>
      </section>
    );
  }

  return (
    <>
      <section
        className="mx-auto mb-4 max-w-[720px] flex flex-wrap justify-between py-8 px-4 bg-white rounded-md
        sm:p-8 sm:pb-8"
      >
        <UserDetailsFields user={user} />
      </section>
      <div className="text-right text-sm mb-8">
        {intl.formatMessage({ id: 'settings.details.updateDetails' })}{' '}
        <button
          type="button"
          onClick={() => {
            openZendesk();
          }}
          className="text-blue-blueMedium font-bold cursor-pointer"
        >
          {intl.formatMessage({ id: 'settings.details.updateDetails.contact' })}
        </button>
      </div>
    </>
  );
};

export default UserDetails;
